import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useAuthStore } from '../../features/auth/hooks'
import type { AdminExtensions } from '../../shared/api'
import { api } from '../../shared/api'
import { useT } from '../../shared/i18n'
import { EMPTY, extensionsForUser, localizeExtensions } from '../registry'

/**
 * Registered admin extensions of enabled plugins. The host only renders
 * declared, granted extension points (docs/03 §4) and never gives plugin
 * frames backend authority.
 */
export function useAdminExtensions() {
  const query = useQuery({
    queryKey: ['admin-extensions'],
    queryFn: () => api<AdminExtensions>('/plugins/extensions/admin'),
    staleTime: 60_000,
  })

  const { locale } = useT()
  const permissions = useAuthStore((s) => s.permissions)

  // Plugin labels in the admin language, falling back to the plugin's default (ADR-044);
  // extensions bound to a permission the user lacks are left out (ADR-071).
  return useMemo(
    () =>
      localizeExtensions(
        extensionsForUser(query.data ?? EMPTY, permissions),
        locale,
      ),
    [query.data, locale, permissions],
  )
}
